import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Dropdown } from "antd";
import {
  deleteGlobalSecureFile,
  getGlobalSecureFileAccessLogs,
  listGlobalSecureFiles,
  regenerateGlobalSecureFileShare,
  revokeGlobalSecureFileShare,
  shareGlobalSecureFile,
  uploadGlobalSecureFile,
} from "../../services/globalSecureFiles";
import SecureFileSearchBar from "../Shared/SecureFileSearchBar";
import SecureFileTableHead from "../Shared/SecureFileTableHead";
import {
  DEFAULT_SECURE_FILE_SORT,
  filterSecureFiles,
  formatUploadedAt,
  formatRetentionExpiresAt,
  sortSecureFiles,
  toggleSecureFileSort,
} from "../../utils/secureFileList";
import "./styles.css";

import { displayDateTime } from "../../utils/dateDisplay";
const MIN_PASSWORD_LENGTH = 4;

const formatFileSize = (bytes) => {
  if (!bytes) {
    return "0 B";
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getShareStatus = (file) => {
  if (!file.shareConfigured) {
    return {
      label: "Not Shared",
      className: "tag is-light",
      linkDetail: "Set a password to activate the share link",
    };
  }
  if (file.shareRevoked) {
    return {
      label: "Revoked",
      className: "tag is-danger",
      linkDetail: "Share link revoked",
    };
  }
  if (!file.shareActive) {
    return {
      label: "Expired",
      className: "tag is-warning",
      linkDetail: `Link expired ${moment(file.shareExpiresAt).format("DD/MM/YYYY")}`,
    };
  }
  return {
    label: "Active",
    className: "tag is-success",
    linkDetail: `Link expires ${moment(file.shareExpiresAt).format("DD/MM/YYYY")}`,
  };
};

const GlobalSecureFiles = () => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedAccessLogs, setSelectedAccessLogs] = useState(null);
  const [isAccessLogsLoading, setIsAccessLogsLoading] = useState(false);
  const [passwordModal, setPasswordModal] = useState(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [confirmPasswordValue, setConfirmPasswordValue] = useState("");
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState(DEFAULT_SECURE_FILE_SORT);

  const loadFiles = useCallback(async () => {
    setIsLoading(true);
    setError("");
    const { data, error: listError } = await listGlobalSecureFiles();
    if (listError) {
      setError(listError.message);
      setFiles([]);
    } else {
      setFiles(data || []);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const filteredFiles = filterSecureFiles(files, searchQuery, getShareStatus);
  const displayedFiles = useMemo(() => sortSecureFiles(filteredFiles, sortConfig), [filteredFiles, sortConfig]);

  const handleSort = (column) => {
    setSortConfig((currentSort) => toggleSecureFileSort(currentSort, column));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = "";
    if (!selectedFile) {
      return;
    }

    if (selectedFile.size > 50 * 1024 * 1024) {
      setError("File exceeds the 50MB limit.");
      return;
    }

    setIsUploading(true);
    setError("");
    setSuccessMessage("");

    const { data, error: uploadError } = await uploadGlobalSecureFile({ file: selectedFile });

    if (uploadError) {
      setError(uploadError.message);
    } else {
      setSuccessMessage(`Uploaded ${data.fileName}. Use Share to set a password and activate the link.`);
      await loadFiles();
    }

    setIsUploading(false);
  };

  const openPasswordModal = (mode, file) => {
    setPasswordModal({ mode, file });
    setPasswordValue("");
    setConfirmPasswordValue("");
    setError("");
  };

  const closePasswordModal = () => {
    setPasswordModal(null);
    setPasswordValue("");
    setConfirmPasswordValue("");
    setIsPasswordSubmitting(false);
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (passwordValue.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (passwordValue !== confirmPasswordValue) {
      setError("Passwords do not match.");
      return;
    }

    setIsPasswordSubmitting(true);
    setError("");

    const { file, mode } = passwordModal;
    let result;

    if (mode === "share") {
      result = await shareGlobalSecureFile({
        shareToken: file.shareToken,
        password: passwordValue,
      });
    } else {
      result = await regenerateGlobalSecureFileShare({
        shareToken: file.shareToken,
        password: passwordValue,
      });
    }

    if (result.error) {
      setError(result.error.message);
      setIsPasswordSubmitting(false);
      return;
    }

    setSuccessMessage(
      mode === "share"
        ? `Share link activated. Expires on ${moment(result.data.shareExpiresAt).format("DD/MM/YYYY")}.`
        : `New share link generated. Expires on ${moment(result.data.shareExpiresAt).format("DD/MM/YYYY")}.`
    );
    closePasswordModal();
    await loadFiles();
  };

  const handleCopyLink = async (shareUrl) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSuccessMessage("Share link copied to clipboard.");
      setError("");
    } catch (copyError) {
      setError("Unable to copy link to clipboard.");
    }
  };

  const handleRevoke = async (shareToken) => {
    const { error: revokeError } = await revokeGlobalSecureFileShare(shareToken);
    if (revokeError) {
      setError(revokeError.message);
      return;
    }
    setSuccessMessage("Share link revoked.");
    await loadFiles();
  };

  const handleDelete = async (shareToken) => {
    const confirmed = window.confirm("Delete this file permanently?");
    if (!confirmed) {
      return;
    }

    const { error: deleteError } = await deleteGlobalSecureFile(shareToken);
    if (deleteError) {
      setError(deleteError.message);
      return;
    }
    setSuccessMessage("File deleted.");
    await loadFiles();
  };

  const handleViewAccessLogs = async (file) => {
    setIsAccessLogsLoading(true);
    setSelectedAccessLogs({ fileName: file.fileName, logs: [] });
    const { data, error: logsError } = await getGlobalSecureFileAccessLogs(file.shareToken);
    if (logsError) {
      setError(logsError.message);
      setSelectedAccessLogs(null);
    } else {
      setSelectedAccessLogs({ fileName: file.fileName, logs: data || [] });
    }
    setIsAccessLogsLoading(false);
  };

  const getMoreMenuItems = (file) => [
    {
      key: "share",
      label: file.shareConfigured && file.shareActive ? "Update Share Password" : "Share File",
    },
    { key: "revoke", label: "Revoke Link", disabled: !file.shareActive },
    { key: "regenerate", label: "New Link" },
    { type: "divider" },
    { key: "delete", label: "Delete File", danger: true },
  ];

  const handleMoreMenuClick = async (file, { key }) => {
    switch (key) {
      case "share":
        openPasswordModal("share", file);
        break;
      case "revoke":
        await handleRevoke(file.shareToken);
        break;
      case "regenerate":
        openPasswordModal("regenerate", file);
        break;
      case "delete":
        await handleDelete(file.shareToken);
        break;
      default:
        break;
    }
  };

  const renderActions = (file) => (
    <div className="secure-file-actions">
      {!file.shareConfigured || !file.shareActive ? (
        <button
          type="button"
          className="button is-small is-info is-light is-radiusless"
          onClick={() => openPasswordModal("share", file)}
        >
          Share
        </button>
      ) : (
        <button
          type="button"
          className="button is-small is-info is-light is-radiusless"
          onClick={() => handleCopyLink(file.shareUrl)}
        >
          Copy Link
        </button>
      )}
      <button
        type="button"
        className="button is-small is-light is-radiusless"
        onClick={() => handleViewAccessLogs(file)}
        disabled={!file.shareConfigured}
      >
        Audit Log
      </button>
      <Dropdown
        menu={{
          items: getMoreMenuItems(file),
          onClick: (info) => handleMoreMenuClick(file, info),
        }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <button
          type="button"
          className="button is-small is-light is-radiusless secure-file-action-trigger"
          aria-label="More file actions"
          onClick={(event) => event.stopPropagation()}
        >
          More
        </button>
      </Dropdown>
    </div>
  );

  const passwordModalTitle = passwordModal?.mode === "regenerate" ? "Generate New Share Link" : "Share File Securely";

  const passwordModalDescription =
    passwordModal?.mode === "regenerate"
      ? "Set a new password for the regenerated link. The previous link will stop working immediately."
      : "Set a password that recipients must enter to download this file. You can share this password separately from the link.";

  return (
    <div className="box is-shadowless secure-file-tab">
      <div className="level mb-4">
        <div className="level-left">
          <div className="level-item">
            <div>
              <p className="heading has-text-grey">Global Secure File Sharing</p>
              <p className="is-size-7 has-text-grey">
                Upload files and share them via password-protected links. Set a custom password each time you share or
                regenerate a link. Links expire after 30 days and can be revoked at any time.
              </p>
            </div>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <input ref={fileInputRef} type="file" hidden onChange={handleFileSelected} />
            <button
              type="button"
              className={`button is-info is-small is-radiusless ${isUploading ? "is-loading" : ""}`}
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              Upload File
            </button>
          </div>
        </div>
      </div>

      {error && !passwordModal && (
        <div className="notification is-danger is-light is-size-7">
          <button className="delete" onClick={() => setError("")} aria-label="dismiss error" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="notification is-success is-light is-size-7">
          <button className="delete" onClick={() => setSuccessMessage("")} aria-label="dismiss success" />
          {successMessage}
        </div>
      )}

      <div className="secure-file-table-wrapper">
        {!isLoading && files.length > 0 && (
          <SecureFileSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            filteredCount={filteredFiles.length}
            totalCount={files.length}
          />
        )}
        {isLoading ? (
          <div className="has-text-centered py-6 has-text-grey">Loading secure files...</div>
        ) : files.length === 0 ? (
          <div className="has-text-centered py-6 has-text-grey">No secure files uploaded yet.</div>
        ) : filteredFiles.length === 0 ? (
          <div className="has-text-centered py-6 has-text-grey">No files match your search.</div>
        ) : (
          <table className="table is-fullwidth is-hoverable is-striped is-narrow">
            <SecureFileTableHead sortConfig={sortConfig} onSort={handleSort} />
            <tbody className="is-size-7">
              {displayedFiles.map((file) => {
                const status = getShareStatus(file);
                return (
                  <tr key={file.shareToken}>
                    <td>{file.fileName}</td>
                    <td>{file.uploadedByName || "Unknown"}</td>
                    <td className="secure-file-uploaded-at">{formatUploadedAt(file.createdAt)}</td>
                    <td className="secure-file-uploaded-at">{formatRetentionExpiresAt(file.retentionExpiresAt)}</td>
                    <td>{formatFileSize(file.fileSize)}</td>
                    <td>
                      <div className="secure-file-share-status">
                        <span className={status.className}>{status.label}</span>
                        <p className="is-size-7 has-text-grey mt-1 mb-0">{status.linkDetail}</p>
                      </div>
                    </td>
                    <td className="secure-file-actions-column">{renderActions(file)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className={`modal ${selectedAccessLogs ? "is-active" : ""}`}>
        <div className="modal-background" onClick={() => setSelectedAccessLogs(null)} />
        <div className="modal-content">
          <div className="box">
            <h3 className="title is-5">Access Audit Log</h3>
            <p className="is-size-7 has-text-grey mb-4">{selectedAccessLogs?.fileName}</p>
            {isAccessLogsLoading ? (
              <p className="has-text-grey">Loading access logs...</p>
            ) : selectedAccessLogs?.logs?.length ? (
              <table className="table is-fullwidth is-narrow is-size-7">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Result</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedAccessLogs.logs.map((log) => (
                    <tr key={log.id}>
                      <td>{displayDateTime(log.accessedAt)}</td>
                      <td>{log.accessType}</td>
                      <td>{log.success ? "Success" : "Failed"}</td>
                      <td>{log.ipAddress || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="has-text-grey">No access attempts recorded yet.</p>
            )}
          </div>
        </div>
        <button className="modal-close is-large" aria-label="close" onClick={() => setSelectedAccessLogs(null)} />
      </div>

      <div className={`modal ${passwordModal ? "is-active" : ""}`}>
        <div className="modal-background" onClick={closePasswordModal} />
        <div className="modal-content">
          <div className="box secure-file-password-modal">
            <h3 className="title is-5">{passwordModalTitle}</h3>
            <p className="is-size-7 has-text-grey mb-4">{passwordModalDescription}</p>
            {passwordModal?.file && (
              <p className="is-size-7 has-text-weight-semibold mb-4">{passwordModal.file.fileName}</p>
            )}
            <form onSubmit={handlePasswordSubmit}>
              <div className="field">
                <label className="label is-size-7" htmlFor="global-share-password">
                  Password
                </label>
                <div className="control">
                  <input
                    id="global-share-password"
                    className="input is-small"
                    type="password"
                    autoComplete="new-password"
                    value={passwordValue}
                    onChange={(event) => setPasswordValue(event.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                </div>
              </div>
              <div className="field">
                <label className="label is-size-7" htmlFor="global-share-password-confirm">
                  Confirm Password
                </label>
                <div className="control">
                  <input
                    id="global-share-password-confirm"
                    className="input is-small"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPasswordValue}
                    onChange={(event) => setConfirmPasswordValue(event.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                </div>
              </div>
              {error && passwordModal && (
                <div className="notification is-danger is-light is-size-7 py-2 px-3 mb-3">{error}</div>
              )}
              <div className="field is-grouped is-grouped-right mt-4">
                <div className="control">
                  <button type="button" className="button is-small is-light is-radiusless" onClick={closePasswordModal}>
                    Cancel
                  </button>
                </div>
                <div className="control">
                  <button
                    type="submit"
                    className={`button is-small is-info is-radiusless ${isPasswordSubmitting ? "is-loading" : ""}`}
                    disabled={isPasswordSubmitting}
                  >
                    {passwordModal?.mode === "regenerate" ? "Generate Link" : "Activate Share Link"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <button className="modal-close is-large" aria-label="close" onClick={closePasswordModal} />
      </div>
    </div>
  );
};

export default GlobalSecureFiles;
